// frontend/src/components/jobPlacement/JobPlacementView.jsx
import React, { useState } from "react";
import { getApiBaseURL } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const JobPlacementView = ({ plan, tenantId, onBack, onEdit }) => {
  const apiBase = getApiBaseURL();
  const [loadingPdf, setLoadingPdf] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleViewPdf = async () => {
    setLoadingPdf(true);
    try {
      const res = await secureAxios.get(
        `${apiBase}/api/job-placements/pdf/${plan.job_placement_id}`,
        { params: { tenant_id: tenantId } }
      );
      if (res.data?.pdf_url) {
        window.open(res.data.pdf_url, "_blank");
      } else {
        toast.error("PDF could not be generated");
      }
    } catch (err) {
      console.error("âŒ PDF generation failed:", err);
      toast.error("Unable to generate or load PDF");
    } finally {
      setLoadingPdf(false);
    }
  };

  if (!plan) {
    return (
      <div className="alert alert-warning">
        <i className="bi bi-exclamation-triangle me-2"></i>
        No plan data available
      </div>
    );
  }

  return (
    <div className="job-placement-view">
      {/* Header with consumer name */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <h5 className="mb-0 h6">
          <i className="bi bi-person-circle me-2 text-primary"></i>
          {plan.consumer_name || "Unknown Consumer"}
        </h5>
        <div className="btn-group">
          <button className="btn btn-outline-secondary" onClick={onBack}>
            <i className="bi bi-arrow-left me-1"></i>
            Back to List
          </button>
          <button className="btn btn-outline-primary" onClick={onEdit}>
            <i className="bi bi-pencil me-1"></i>
            Edit Plan
          </button>
          <button
            className="btn btn-primary"
            onClick={handleViewPdf}
            disabled={loadingPdf}
          >
            {loadingPdf ? (
              <>
                <span className="spinner-border spinner-border-sm me-1"></span>
                Loading...
              </>
            ) : (
              <>
                <i className="bi bi-file-pdf me-1"></i>
                View PDF
              </>
            )}
          </button>
        </div>
      </div>

      {/* Plan Details */}
      <div className="row g-2 g-md-4">
        {/* Basic Information */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-info-circle me-2"></i>
                Plan Information
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <div className="row g-2 g-md-3">
                <div className="col-md-6">
                  <p>
                    <strong>Consumer:</strong> {plan.consumer_name || "-"}
                  </p>
                </div>
                <div className="col-md-6">
                  <p>
                    <strong>Plan Date:</strong> {formatDate(plan.date)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Skills Section */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-lightbulb me-2"></i>
                Specific Skills, Aptitudes, Interests, Abilities
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {plan.skills || "No skills recorded"}
              </p>
            </div>
          </div>
        </div>

        {/* Vocational Goal */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-bullseye me-2"></i>
                Vocational Goal
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {plan.vocational_goal || "No vocational goal recorded"}
              </p>
            </div>
          </div>
        </div>

        {/* Job Titles Table */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-briefcase me-2"></i>
                Job Titles
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              {plan.job_titles && plan.job_titles.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Job Title</th>
                        <th>Availability in Community</th>
                      </tr>
                    </thead>
                    <tbody>
                      {plan.job_titles.map((t, i) => (
                        <tr key={i}>
                          <td>{t.job_title || "-"}</td>
                          <td>{t.availability_in_community || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted mb-0">No job titles recorded</p>
              )}
            </div>
          </div>
        </div>

        {/* Support Needs */}
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-hand-thumbs-up me-2"></i>
                Support Needs
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              {plan.support_needs && plan.support_needs.length > 0 ? (
                <ol className="mb-0">
                  {plan.support_needs.map((need, i) => (
                    <li key={i}>{need || "-"}</li>
                  ))}
                </ol>
              ) : (
                <p className="text-muted mb-0">No support needs recorded</p>
              )}
            </div>
          </div>
        </div>

        {/* Job Search Notes */}
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-search me-2"></i>
                Additional Job Search Notes
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              {plan.job_search_notes &&
              plan.job_search_notes.length > 0 &&
              plan.job_search_notes.some((n) => n && n.trim()) ? (
                <ul className="mb-0 list-unstyled">
                  {plan.job_search_notes
                    .filter((n) => n && n.trim())
                    .map((note, i) => (
                      <li key={i} className="mb-1">
                        <strong>{String.fromCharCode(69 + i)}.</strong> {note}
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">
                  No additional job search notes
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Job Placement Notes */}
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-check2-square me-2"></i>
                Additional Job Placement Notes
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              {plan.job_placement_notes &&
              plan.job_placement_notes.length > 0 &&
              plan.job_placement_notes.some((n) => n && n.trim()) ? (
                <ul className="mb-0 list-unstyled">
                  {plan.job_placement_notes
                    .filter((n) => n && n.trim())
                    .map((note, i) => (
                      <li key={i} className="mb-1">
                        <strong>{String.fromCharCode(68 + i)}.</strong> {note}
                      </li>
                    ))}
                </ul>
              ) : (
                <p className="text-muted mb-0">
                  No additional job placement notes
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Additional Input */}
        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-chat-left-text me-2"></i>
                Additional Consumer Input
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {plan.consumer_input || "No additional consumer input"}
              </p>
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card h-100">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-chat-right-text me-2"></i>
                Additional Counselor Input
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <p className="mb-0" style={{ whiteSpace: "pre-wrap" }}>
                {plan.counselor_input || "No additional counselor input"}
              </p>
            </div>
          </div>
        </div>

        {/* Signatures Section */}
        <div className="col-12">
          <div className="card">
            <div className="card-header bg-light py-2">
              <strong>
                <i className="bi bi-pen me-2"></i>
                Signatures
              </strong>
            </div>
            <div className="card-body p-2 p-md-3">
              <div className="row">
                {/* Consumer Signature */}
                <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
                  <h6>Consumer Signature</h6>
                  {plan.consumer_signature ? (
                    <div className="border rounded p-2 bg-white mb-2">
                      <img
                        src={plan.consumer_signature}
                        alt="Consumer Signature"
                        style={{ maxWidth: "100%", maxHeight: "80px" }}
                      />
                    </div>
                  ) : (
                    <div className="border rounded p-3 bg-light text-muted text-center mb-2">
                      No signature
                    </div>
                  )}
                  <small className="text-muted">
                    Date: {formatDate(plan.consumer_signature_date)}
                  </small>
                </div>

                {/* CRP Signature */}
                <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
                  <h6>CRP Placement Specialist</h6>
                  {plan.crp_signature ? (
                    <div className="border rounded p-2 bg-white mb-2">
                      <img
                        src={plan.crp_signature}
                        alt="CRP Signature"
                        style={{ maxWidth: "100%", maxHeight: "80px" }}
                      />
                    </div>
                  ) : (
                    <div className="border rounded p-3 bg-light text-muted text-center mb-2">
                      No signature
                    </div>
                  )}
                  <small className="text-muted">
                    Date: {formatDate(plan.crp_signature_date)}
                  </small>
                </div>

                {/* WVDRS Signature */}
                <div className="col-12 col-sm-6 col-md-4 mb-2 mb-md-3">
                  <h6>WVDRS Counselor</h6>
                  {plan.wvdrs_signature ? (
                    <div className="border rounded p-2 bg-white mb-2">
                      <img
                        src={plan.wvdrs_signature}
                        alt="WVDRS Signature"
                        style={{ maxWidth: "100%", maxHeight: "80px" }}
                      />
                    </div>
                  ) : (
                    <div className="border rounded p-3 bg-light text-muted text-center mb-2">
                      No signature
                    </div>
                  )}
                  <small className="text-muted">
                    Date: {formatDate(plan.wvdrs_signature_date)}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Timestamps */}
        <div className="col-12">
          <div className="card bg-light">
            <div className="card-body p-2">
              <div className="row text-muted small">
                <div className="col-md-6">
                  <i className="bi bi-clock me-1"></i>
                  Created: {formatDate(plan.created_at)}
                </div>
                <div className="col-md-6">
                  <i className="bi bi-clock-history me-1"></i>
                  Last Updated: {formatDate(plan.updated_at)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JobPlacementView;